import { Service, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxOption } from '@onyx/angular';
import { AddressHelper } from '@onyx/angular';
import type { Observable } from 'rxjs';
import { concatMap, from, map, merge, mergeMap, switchMap, tap } from 'rxjs';
import { AuthStore } from '../../../../../auth/common/stores/auth.store';
import { ApiService } from '../../../../../common/services/api.service';
import type { BusinessFormDto } from '../../business/business.component';
import type { Business } from '../interfaces/business';
import type { BusinessBranch } from '../interfaces/business-branch';

@Service()
export class BusinessService extends ApiService {
  private readonly authStore = inject(AuthStore);
  private readonly translateService = inject(TranslateService);

  public getBusiness(): Observable<Business> {
    return this.get<Business>('/business').pipe(
      tap((business) => this.authStore.setBusiness(business)),
    );
  }

  public updateBusiness(dto: BusinessFormDto): Observable<void> {
    const previousBranchesUuid = new Set(
      this.authStore.business()!.branches.map((branch) => branch.uuid),
    );
    const { branches, ...companyDto } = dto;

    const addBranches$ = from(
      branches.filter(({ uuid }) => !previousBranchesUuid.has(uuid!)),
    ).pipe(concatMap((branch) => this.addBranch(branch)));
    const editBranches$ = from(
      branches.filter(({ uuid }) => previousBranchesUuid.has(uuid!)),
    ).pipe(mergeMap((branch) => this.editBranch(branch)));
    const deleteBranches$ = from(
      [...previousBranchesUuid.values()].filter(
        (uuid) => !branches.some((branch) => branch.uuid === uuid),
      ),
    ).pipe(mergeMap((uuid) => this.deleteBranch(uuid)));

    return merge(
      this.put('/business', companyDto),
      addBranches$,
      editBranches$,
      deleteBranches$,
    ).pipe(
      switchMap(() => this.getBusiness()),
      tap((business) => this.authStore.setBusiness(business)),
      map(() => undefined),
    );
  }

  public getBranches(): Observable<OnyxOption<string>[]> {
    const buildOption = (
      business: Business,
      branch: Business | BusinessBranch,
    ): OnyxOption<string> => {
      const { mainAddress } = branch;
      const vatId =
        ('vatIdentification' in branch ? branch.vatIdentification : null) ??
        business;

      return {
        name: 'name' in branch ? branch.name : 'labels.mainBranch',
        value: branch.uuid,
        leftFlag: mainAddress.countryCode,
        description: AddressHelper.composeLabel(mainAddress),
        hint: `${this.translateService.instant('labels.vatId')}: ${vatId.country.toUpperCase()}${vatId.vatId}`,
      };
    };

    return this.getBusiness().pipe(
      map((business) => [
        buildOption(business, business),
        ...business.branches.map((branch) => buildOption(business, branch)),
      ]),
    );
  }

  public updateBusinessLogo(logo: File): Observable<void> {
    const data = new FormData();
    data.set('logo', logo);

    return this.post<{ storageUuid: string }>('/business/logo', data).pipe(
      tap(({ storageUuid }) =>
        this.authStore.updateBusiness({ logo: storageUuid }),
      ),
      map(() => undefined),
    );
  }

  public deleteBusinessLogo(): Observable<void> {
    return this.delete<void>('/business/logo').pipe(
      tap(() => this.authStore.updateBusiness({ logo: undefined })),
    );
  }

  private addBranch(
    branch: BusinessFormDto['branches'][number],
  ): Observable<void> {
    return this.post(`/business/branches`, branch);
  }

  private editBranch(
    branch: BusinessFormDto['branches'][number],
  ): Observable<void> {
    return this.put(`/business/branches/${branch.uuid}`, branch);
  }

  private deleteBranch(uuid: string): Observable<void> {
    return this.delete(`/business/branches/${uuid}`);
  }
}
